import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { MouseEvent, useState } from 'react';
import OrganizationEditDialog from './organization-edit-dialog';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SharedProfileInformationAccordion from '../../../../shared/shared-profile-information-accordion';
import SharedProfileInformationItemActions from '../../../../shared/shared-profile-information-item-actions';

interface OrganizationsAccordionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  setValue: UseFormSetValue<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  getValues: UseFormGetValues<UserFormData>;
  disabled: boolean;
}

export default function OrganizationsAccordion({
  control,
  errors,
  disabled,
  watch,
  setValue,
  getValues,
}: OrganizationsAccordionProps) {
  const [openOrganizationDialog, setOpenOrganizationDialog] = useState(false);
  const [clickedIndex, setClickedIndex] = useState(0);

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'organizations',
  });

  const menuItems = [
    { value: 'custom', label: 'Custom' },
    { value: 'domain_only', label: 'Domain Only' },
    { value: 'school', label: 'School' },
    { value: 'unknown', label: 'Unknown' },
    { value: 'work', label: 'Work' },
  ];

  const orgErrors = errors['organizations'];
  const orgCount = fields.length;

  const handleOnAdd = (
    event: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation();
    append({
      name: '',
      title: '',
      primary: fields.find((item) => item.primary) ? false : true,
      type: 'work',
      customType: '',
      department: '',
      symbol: '',
      location: '',
      description: '',
      domain: '',
      costCenter: '',
    });
  };

  return (
    <>
      <SharedProfileInformationAccordion
        title="Organizations"
        count={orgCount}
        hasError={!!orgErrors}
        onAdd={handleOnAdd}
        disabled={disabled}
      >
        {fields.map((item, index) => {
          const typeField = watch(`organizations.${index}.type`);

          return (
            <Box key={item.id}>
              <Stack
                gap={1}
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ my: 1.5 }}
              >
                <Stack flexDirection="row" alignItems="center" gap={2}>
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`organizations.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `organizations-type-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `organizations-type-${index}`,
                        }}
                        name="organizations"
                        select
                        disabled={disabled}
                        label="Type"
                        error={!!orgErrors?.[index]?.type}
                        helperText={
                          orgErrors?.[index]?.type
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 150,
                        }}
                      >
                        {menuItems.map((item) => (
                          <MenuItem value={item.value} key={item.label}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  {typeField === 'custom' && (
                    <Controller
                      rules={{ required: 'This field is required.' }}
                      name={`organizations.${index}.customType`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          inputRef={field.ref}
                          inputProps={{
                            id: `organizations-customType-${index}`,
                          }}
                          InputLabelProps={{
                            htmlFor: `organizations-customType-${index}`,
                          }}
                          disabled={disabled}
                          name="organizations"
                          label="Custom Type"
                          error={!!orgErrors?.[index]?.customType}
                          helperText={
                            orgErrors?.[index]?.customType
                              ? 'This field is required.'
                              : ''
                          }
                          sx={{
                            minWidth: 160,
                          }}
                        />
                      )}
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {`${
                      watch(`organizations.${index}.name`) || 'No name provided'
                    }`}
                  </Typography>
                </Stack>

                <SharedProfileInformationItemActions
                  index={index}
                  totalCount={orgCount}
                  disabled={disabled}
                  onMoveDown={() => move(index, index + 1)}
                  onMoveUp={() => move(index, index - 1)}
                  onEdit={() => {
                    setClickedIndex(index);
                    setOpenOrganizationDialog(true);
                  }}
                  editTooltipLabel="Edit Organization"
                  onDelete={() => remove(index)}
                >
                  <Controller
                    name={`organizations.${index}.primary`}
                    control={control}
                    render={({ field }) => {
                      return (
                        <FormControlLabel
                          control={
                            <Tooltip title="This determines what is shown in Google Admin panel.">
                              <Checkbox
                                onChange={() => {
                                  field.onChange(!field.value);

                                  // set all other primary values to false
                                  fields.forEach((_item, i) => {
                                    if (i !== index) {
                                      setValue(
                                        `organizations.${i}.primary`,
                                        false
                                      );
                                    }
                                  });
                                }}
                                size="small"
                                checked={field.value === true}
                              />
                            </Tooltip>
                          }
                          label="primary"
                        />
                      );
                    }}
                  />
                </SharedProfileInformationItemActions>
              </Stack>
              {index !== fields.length - 1 && <Divider />}
            </Box>
          );
        })}
      </SharedProfileInformationAccordion>
      {openOrganizationDialog ? (
        <OrganizationEditDialog
          openOrganizationDialog={openOrganizationDialog}
          setOpenOrganizationDialog={setOpenOrganizationDialog}
          clickedIndex={clickedIndex}
          setValue={setValue}
          disabled={!!disabled}
          getValues={getValues}
        />
      ) : null}
    </>
  );
}
